import axios from 'axios';

const getBaseUrl = () => {
  return '/api';
};

const API_BASE_URL = getBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear token and redirect to login only on 401 (Unauthorized)
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    // We don't auto-logout on 403 because it might mean "account pending" or "email unverified"
    return Promise.reject(error);
  }
);

export const authService = {
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
  register: (email, username, password, paypal_link) => apiClient.post('/auth/register', { email, username, password, paypal_link }),
  getMe: () => apiClient.get('/auth/me'),
  updateProfile: (data) => apiClient.post('/auth/update-profile', data),
  requestPasswordReset: (email) => apiClient.post('/auth/request-password-reset', { email }),
  resetPassword: (email, code, new_password) => apiClient.post('/auth/reset-password', { email, code, new_password }),
  verifyEmail: (code) => apiClient.post('/auth/verify-email', { code }),
  resendVerification: () => apiClient.post('/auth/resend-verification'),
};

export const marketService = {
  getAll: (params) => apiClient.get('/markets', { params: { limit: 20, ...params } }),
  getById: (id) => apiClient.get(`/markets/${id}`),
  create: (data) => apiClient.post('/markets', data),
  update: (id, data) => apiClient.patch(`/markets/${id}`, data),
  bet: (marketId, data) => apiClient.post('/bets', { market_id: marketId, ...data }),
  resolve: (marketId, data) => apiClient.post(`/markets/${marketId}/resolve`, data),
  cancel: (marketId) => apiClient.post(`/markets/${marketId}/cancel`),
  join: (marketId) => apiClient.post(`/markets/${marketId}/join`),
};

export const notificationService = {
  getAll: (params) => apiClient.get('/notifications', { params: { limit: 20, ...params } }),
  markAsRead: (id) => apiClient.post(`/notifications/${id}/read`),
  markAllAsRead: () => apiClient.post('/notifications/read-all'),
};

export const userService = {
  getById: (id) => apiClient.get(`/users/${id}`),
  getStats: (id) => apiClient.get(`/users/${id}/stats`),
  getAll: () => apiClient.get('/users'),
};

export const organizationService = {
  getAll: () => apiClient.get('/organizations'),
  join: (id, code, paypal_link) => apiClient.post(`/organizations/${id}/join`, { code, paypal_link }),
  request: (id) => apiClient.post(`/organizations/${id}/request`),
  leave: (id) => apiClient.post(`/organizations/${id}/leave`),
  getRequests: (id) => apiClient.get(`/organizations/${id}/requests`),
  approve: (id, rid) => apiClient.post(`/organizations/${id}/requests/${rid}/approve`),
  reject: (id, rid) => apiClient.post(`/organizations/${id}/requests/${rid}/reject`),
  create: (data) => apiClient.post('/organizations', data),
  update: (id, data) => apiClient.patch(`/organizations/${id}`, data),
  getMembers: (id) => apiClient.get(`/organizations/${id}/members`),
  removeMember: (id, userId) => apiClient.delete(`/organizations/${id}/members/${userId}`),
};

export default apiClient;
